import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  ApplicationConfig,
  ProductConfig,
  SectionHeaderInfo,
  QuoteConfig,
  ExternalAuthConfig
} from "stere-insurance-sdk/dist/types";

import { ClientProduct } from "customTypes/ClientProducts";

interface ClientState {
  clientAPIKey: string;
  clientUrl: string;
  noQuoteText: string;
  customerId: string;
  applicantId: string;
  productSelection: string[];
  availableProducts: ClientProduct[];
  startPageSelection: ClientProduct[];
  selectedProducts: string[]; 
  plansSelected: any[];
  loading: boolean;
  loadingClientFeatures: boolean;
  sectionHeaderInfoConfig: SectionHeaderInfo[];
  invalidProductCombinations: string[];
  showPaymentSummaryPage: boolean;
  showAutoRenewalSwitch: boolean;
  autoRenewalConsent: boolean;
  errorAndShowExitBtn: boolean;
  clientFeatures: ClientFeatures;
  externalAuthConfig: ExternalAuthConfig;
  simulateErrorConfig: SimulateErrorConfig;
  applicationConfig: ApplicationConfig;
  productConfig: ProductConfig;
  quoteConfig: QuoteConfig;
  supportEmail: string;
}

const initialState: ClientState = {
  clientAPIKey: "",
  clientUrl: "",
  noQuoteText: "",
  customerId: "",
  applicantId: "",
  productSelection: [],
  availableProducts: [],
  startPageSelection: [],
  selectedProducts: [],
  plansSelected: [],
  loading: false,
  loadingClientFeatures: false,
  sectionHeaderInfoConfig: [],
  invalidProductCombinations: [],
  showPaymentSummaryPage: true,
  showAutoRenewalSwitch: false,
  autoRenewalConsent: false,
  errorAndShowExitBtn: false,
  clientFeatures: null,
  simulateErrorConfig: null,
  applicationConfig: null,
  productConfig: null,
  quoteConfig: null,
  externalAuthConfig: null,
  supportEmail: null,
};

const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    saveClientSentInfo(state, action: PayloadAction<any>) {
      state.clientAPIKey = action.payload["X-API-KEY"];
      state.applicantId = action.payload.applicant_id;
      state.productSelection = action.payload.product_selection;
    },
    saveNoQuoteText(state, action: PayloadAction<string>) {
      state.noQuoteText = action.payload;
    },
    fetchAvailableProducts(state) {
      state.loading = true;
    },
    setAvailableProducts(state, action: PayloadAction<ClientProduct[]>) {
      state.availableProducts = action.payload;
      state.loading = false;
    },
    setLoadingValue(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    updateStartPageSelection(state, action: PayloadAction<ClientProduct[]>) {
      state.startPageSelection = action.payload;
      state.selectedProducts = action.payload.map(p => `${p?.product?.product_identifier}-${p?.carrier?.[0]}`)
    },
    setCustomerId(state, action: PayloadAction<string>) {
      state.customerId = action.payload;
    },
    setClientUrl(state, action: PayloadAction<string>) {
      state.clientUrl = action.payload;
    },
    setSectionHeaderInfoConfig(
      state,
      action: PayloadAction<SectionHeaderInfo[]>
    ) {
      state.sectionHeaderInfoConfig = action.payload;
    },
    addInvalidProductCombinations(state, action: PayloadAction<string>) {
      state.invalidProductCombinations = [
        ...state?.invalidProductCombinations,
        action.payload,
      ];
    },
    removeInvalidProductCombinations(state, action: PayloadAction<string>) {
      state.invalidProductCombinations = state?.invalidProductCombinations.filter(
        (p) => p !== action?.payload
      );
    },
    setShowPaymentSummaryPage(state, action: PayloadAction<boolean>) {
      state.showPaymentSummaryPage = action.payload;
    },
    setShowAutoRenewalSwitch(state, action: PayloadAction<boolean>) {
      state.showAutoRenewalSwitch = action.payload;
    },
    toggleAutoRenewalConsent(state, action: PayloadAction<boolean>) {
      state.autoRenewalConsent = action.payload
    },
    setErrorAndShowExitBtn(state, action: PayloadAction<boolean>) {
      state.errorAndShowExitBtn = action.payload;
    },
    fetchClientFeatures(state) {
      state.loadingClientFeatures = true;
    },
    setClientFeatures(state, action: PayloadAction<ClientFeatures>) {
      state.clientFeatures = action.payload;
      state.loadingClientFeatures = false;
    },
    setSimulateErrorConfig(state, action: PayloadAction<SimulateErrorConfig>) {
      state.simulateErrorConfig = action.payload;
    },
    setApplicationConfig(state, action: PayloadAction<ApplicationConfig>) {
      state.applicationConfig = action.payload;
    },
    setProductConfig(state, action: PayloadAction<ProductConfig>) {
      state.productConfig = action.payload;
    },
    setQuoteConfig(state, action: PayloadAction<QuoteConfig>) {
      state.quoteConfig = action.payload;
    },
    setExternalAuthConfig(state, action: PayloadAction<ExternalAuthConfig>) {
      state.externalAuthConfig = action.payload;
    },
    setSupportEmail(state, action: PayloadAction<string>) {
      state.supportEmail = action.payload;
    }
  },
});

export const {
  saveClientSentInfo,
  saveNoQuoteText,
  fetchAvailableProducts,
  setAvailableProducts,
  setLoadingValue,
  updateStartPageSelection,
  setCustomerId,
  setClientUrl,
  setSectionHeaderInfoConfig,
  addInvalidProductCombinations,
  removeInvalidProductCombinations,
  setShowPaymentSummaryPage,
  setShowAutoRenewalSwitch,
  toggleAutoRenewalConsent,
  setErrorAndShowExitBtn,
  fetchClientFeatures,
  setClientFeatures,
  setSimulateErrorConfig,
  setApplicationConfig,
  setProductConfig,
  setQuoteConfig,
  setSupportEmail,
  setExternalAuthConfig,
} = clientSlice.actions;
export default clientSlice.reducer;
